import React, { useState, useEffect } from 'react';
import { extractFlowElementsFromMetadata, extractFlowVariablesFromMetadata } from '../utils/flowUtils';
import { useCollapsibleState } from '../hooks/useCollapsibleState';
import { supabase } from '../services/supabaseClient';

interface FlowElementsDisplayProps {
  flow: any;
}

const FlowElementsDisplay: React.FC<FlowElementsDisplayProps> = ({ flow }) => {
  const [isExpanded, setIsExpanded] = useCollapsibleState('flowElements', false);
  const [elementsData, setElementsData] = useState<any[]>([]);
  const [variablesData, setVariablesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch elements and variables from database
  useEffect(() => {
    const fetchData = async () => {
      if (!flow?.id) return;
      
      try {
        // Fetch elements from sf_flow_elements
        const { data: elements, error: elementsError } = await supabase
          .from('sf_flow_elements')
          .select('*')
          .eq('flow_definition_id', flow.id)
          .order('element_type', { ascending: true });
          
        if (elementsError) {
          console.error('Error fetching flow elements:', elementsError);
        } else {
          setElementsData(elements || []);
        }
        
        // Fetch variables from sf_flow_variables
        const { data: variables, error: variablesError } = await supabase
          .from('sf_flow_variables')
          .select('*')
          .eq('flow_definition_id', flow.id)
          .order('data_type', { ascending: true });
          
        if (variablesError) {
          console.error('Error fetching flow variables:', variablesError);
        } else {
          setVariablesData(variables || []);
        }
      } catch (error) {
        console.error('Error fetching flow data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [flow?.id]);
  
  // Fallback to metadata extraction if no data in tables
  const extractedElements: any[] = elementsData.length > 0 ? elementsData : [];
  const extractedVariables: any[] = variablesData.length > 0 ? variablesData : [];
  
  // If no data from database, try extracting from metadata
  if (extractedElements.length === 0 && flow.metadata) {
    const flowElements = extractFlowElementsFromMetadata(flow.metadata);
    if (flowElements) {
      const elementTypes = [
        'recordLookups', 'recordUpdates', 'recordCreates', 'recordDeletes',
        'decisions', 'screens', 'assignments', 'actionCalls', 'subflows',
        'loops', 'waits', 'start'
      ];
      
      elementTypes.forEach(type => {
        if (flowElements[type]) {
          const items = Array.isArray(flowElements[type]) ? flowElements[type] : [flowElements[type]];
          items.forEach((element: any) => {
            if (element) {
              extractedElements.push({
                ...element,
                element_type: type,
                label: element.label || element.name || 'Unnamed'
              });
            }
          });
        }
      });
    }
  }
  
  if (extractedVariables.length === 0 && flow.metadata) {
    const flowVariables = extractFlowVariablesFromMetadata(flow.metadata);
    if (flowVariables) {
      const variableTypes = [
        'variables', 'constants', 'formulas', 'textTemplates', 
        'choices', 'dynamicChoiceSets', 'stages'
      ];
      
      variableTypes.forEach(type => {
        if (flowVariables[type]) {
          const items = Array.isArray(flowVariables[type]) ? flowVariables[type] : [flowVariables[type]];
          items.forEach((variable: any) => {
            if (variable) {
              extractedVariables.push({
                ...variable,
                api_name: variable.name,
                data_type: variable.dataType || type
              });
            }
          });
        }
      });
    }
  }
  
  // Check if we have valid data with our extracted arrays
  const hasElements = extractedElements.length > 0;
  const hasVariables = extractedVariables.length > 0;

  // Debug the data we have
  console.log('FlowElementsDisplay data:', {
    hasElements,
    hasVariables,
    elementsCount: extractedElements.length,
    variablesCount: extractedVariables.length,
    fromDatabase: elementsData.length > 0 || variablesData.length > 0,
    loading
  });

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
      <div className="px-4 py-5 sm:px-6 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Flow Elements and Variables</h3>
          <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform ${isExpanded ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {isExpanded && (
        <div className="border-t border-gray-200 p-4">
          {!hasElements && !hasVariables ? (
            <p className="text-gray-500 italic text-sm">No flow elements or variables available.</p>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {/* Flow Elements Section */}
      <div className="bg-white p-4 border rounded-md shadow-sm">
        <h3 className="text-lg leading-6 font-medium text-gray-900 text-left mb-4">Flow Elements</h3>
        
        {loading ? (
          <p className="text-gray-500 italic text-sm">Loading elements...</p>
        ) : hasElements ? (
          <div className="overflow-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {extractedElements.map((element: any, index: number) => {
                  // Extract object from properties if available
                  const object = element.properties?.object || element.object || '';
                  const elementType = element.element_type || element.ElementType || 'Unknown';
                  
                  return (
                    <tr key={element.id || element.element_id || element.name || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{element.label || element.name || 'Unnamed'}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {elementType}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {element.description || (object ? `Object: ${object}` : '-')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 italic text-sm">No flow elements available.</p>
        )}
      </div>

      {/* Flow Variables Section */}
      <div className="bg-white p-4 border rounded-md shadow-sm">
        <h3 className="text-lg leading-6 font-medium text-gray-900 text-left mb-4">Flow Variables</h3>
        
        {loading ? (
          <p className="text-gray-500 italic text-sm">Loading variables...</p>
        ) : hasVariables ? (
          <div className="overflow-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Properties</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {extractedVariables.map((variable: any, index: number) => {
                  const dataType = variable.data_type || variable.dataType || variable.VariableType || 'Text';
                  const varName = variable.api_name || variable.name || 'Unnamed';
                  
                  // Build properties display
                  const properties: string[] = [];
                  if (variable.is_collection) properties.push('Collection');
                  if (variable.is_input) properties.push('Input');
                  if (variable.is_output) properties.push('Output');
                  
                  return (
                    <tr key={variable.id || varName || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{varName}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {dataType}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {properties.length > 0 ? (
                          <div className="flex gap-1">
                            {properties.map(prop => (
                              <span key={prop} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {prop}
                              </span>
                            ))}
                          </div>
                        ) : (
                          variable.description || '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 italic text-sm">No flow variables available.</p>
        )}
      </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FlowElementsDisplay;